import { useState, useMemo, forwardRef, useRef, useEffect } from 'react'
import type { FieldError as HookFieldError } from 'react-hook-form'

import { getAmericaEuropeTimezones } from '@/lib/utils'
import { cn } from '@/lib/utils'
import useClickOutside from '@/lib/hooks/useClickOutside'
import FieldError from '@/components/livecontact/FieldError'
import LcIcon from '@/components/livecontact/icons'
import {
  INPUT_CLASS,
  INPUT_ERROR_CLASS,
  INPUT_OPEN_CLASS,
  LABEL_CLASS,
  OPTION_CLASS,
  POP_CLASS,
} from '@/components/livecontact/fieldStyles'

type TimezoneOption = {
  value: string
  label: string
}

type Props = {
  label?: string
  className?: string
  classInput?: string
  classLabel?: string
  classButton?: string
  classOptions?: string
  classOption?: string
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  onBlur?: () => void
  error?: HookFieldError
  required?: boolean
  disabled?: boolean
}

/** Searchable timezone combobox on the field surface. */
const TimezoneSelectField = forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      className,
      classInput,
      classLabel,
      classButton,
      classOptions,
      classOption,
      placeholder = 'Select timezone',
      value,
      onChange,
      onBlur,
      error,
      required,
      disabled,
    },
    ref
  ) => {
    const [query, setQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const internalInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (typeof ref === 'function') {
        ref(internalInputRef.current)
      } else if (ref) {
        ;(ref as React.MutableRefObject<HTMLInputElement | null>).current = internalInputRef.current
      }
    }, [ref])

    const allTimezones = useMemo(() => getAmericaEuropeTimezones(), [])

    const filteredTimezones = useMemo(() => {
      if (!query) return allTimezones
      const lowerQuery = query.toLowerCase()
      return allTimezones.filter(
        (tz) =>
          tz.label.toLowerCase().includes(lowerQuery) || tz.value.toLowerCase().includes(lowerQuery)
      )
    }, [query, allTimezones])

    const selectedTimezone = useMemo(
      () => allTimezones.find((tz) => tz.value === value) || null,
      [value, allTimezones]
    )

    const handleClickOutside = () => {
      setIsOpen(false)
      setQuery('')
    }
    useClickOutside(dropdownRef, handleClickOutside, isOpen)

    const handleSelect = (timezone: TimezoneOption) => {
      onChange?.(timezone.value)
      setIsOpen(false)
      setQuery('')
      internalInputRef.current?.blur()
    }

    const toggleDropdown = () => {
      if (disabled) return
      setIsOpen(!isOpen)
      if (!isOpen) internalInputRef.current?.focus()
    }

    return (
      <div className={cn('relative', className)}>
        {label && (
          <div className={cn(LABEL_CLASS, classLabel)}>
            {label}
            {required && <span className="ml-0.5 text-[#D33]">*</span>}
          </div>
        )}
        <div ref={dropdownRef}>
          <div className="relative">
            <input
              ref={internalInputRef}
              type="text"
              role="combobox"
              aria-expanded={isOpen}
              className={cn(INPUT_CLASS, 'pr-8', isOpen && INPUT_OPEN_CLASS, error && INPUT_ERROR_CLASS, classInput)}
              value={isOpen || query ? query : selectedTimezone?.label || ''}
              onChange={(e) => {
                setQuery(e.target.value)
                setIsOpen(true)
              }}
              onFocus={() => {
                setIsOpen(true)
                setQuery('')
              }}
              onBlur={() => setTimeout(() => onBlur?.(), 200)}
              placeholder={placeholder}
              disabled={disabled}
              autoComplete="off"
              aria-invalid={!!error}
            />
            <button
              type="button"
              className={cn(
                'absolute inset-y-0 right-0 flex items-center pr-2 text-ink-4 outline-none disabled:opacity-50',
                classButton
              )}
              onClick={toggleDropdown}
              tabIndex={-1}
              disabled={disabled}
              aria-label="Toggle timezones"
            >
              <LcIcon
                name="chevron-down"
                className={cn('transition-transform duration-[180ms] ease-out-strong', isOpen && 'rotate-180')}
              />
            </button>
          </div>

          {isOpen && (
            <div
              role="listbox"
              className={cn(
                'absolute left-0 right-0 z-20 mt-1.5 max-h-60 w-full origin-top overflow-auto animate-lc-pop lc-scrollbar-thin',
                POP_CLASS,
                classOptions
              )}
            >
              {filteredTimezones.length === 0 ? (
                <div className="px-2.5 py-3 text-center text-[13px] text-ink-4">No timezones found</div>
              ) : (
                filteredTimezones.map((timezone) => {
                  const isSelected = timezone.value === value
                  return (
                    <div
                      key={timezone.value}
                      role="option"
                      aria-selected={isSelected}
                      className={cn(OPTION_CLASS, 'justify-between', isSelected && 'text-ink', classOption)}
                      onClick={() => handleSelect(timezone)}
                    >
                      <span className="truncate">{timezone.label}</span>
                      {isSelected && <LcIcon name="check" className="ml-2 shrink-0 text-brand" />}
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>

        <FieldError message={error?.message} />
      </div>
    )
  }
)

TimezoneSelectField.displayName = 'TimezoneSelectField'

export default TimezoneSelectField
